import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ExternalLink, Facebook, FileText, Instagram, Mail, MapPin, MessageCircle, Phone, Scale } from 'lucide-react';
import { companyInfo } from '../../data/mockData';
import { trackEvent } from '../../utils/analytics';

const legalLinks = [
  { to: '/aviso-legal', label: 'Aviso Legal' },
  { to: '/politica-privacidade', label: 'Política de Privacidade' },
  { to: '/termos-condicoes', label: 'Termos e Condições' },
  { to: '/politica-cookies', label: 'Política de Cookies' },
  { to: '/resolucao-litigios', label: 'Resolução de Litígios' },
];

const Footer = () => {
  return (
    <footer className="bg-[#050505] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="space-y-5">
            <Link to="/" className="flex items-center">
              <img
                src="/logo.png"
                alt="BestCars"
                width="160"
                height="48"
                loading="lazy"
                decoding="async"
                className="h-12 w-auto object-contain"
              />
            </Link>
            <p className="text-white/40 text-sm leading-relaxed">
              A sua escolha de confiança no mundo automóvel. Qualidade, transparência e preços competitivos.
            </p>
            <div className="flex gap-3">
              <a
                href={companyInfo.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                href={companyInfo.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={companyInfo.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent('whatsapp_click', { location: 'footer' })}
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/40 hover:text-[#25D366] hover:bg-[#25D366]/10 hover:border-[#25D366]/20 transition-all duration-300"
                aria-label="WhatsApp"
              >
                <MessageCircle size={16} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-6">Navegação</h4>
            <ul className="space-y-3">
              {[
                { to: '/', label: 'Início' },
                { to: '/viaturas', label: 'Viaturas' },
                { to: '/sobre', label: 'Sobre Nós' },
                { to: '/contacto', label: 'Contacto' },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-white/40 hover:text-white text-sm transition-colors duration-300 hover:pl-1">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-6">Serviços</h4>
            <ul className="space-y-3">
              {[
                { to: '/servicos/oficina', label: 'Serviços de Oficina' },
                { to: '/servicos/consultadoria', label: 'Encomenda de Viatura' },
                { to: '/servicos/credito', label: 'Intermediação de Crédito' },
                { to: '/servicos/compramos', label: 'Compramos o seu Carro' },
              ].map((service) => (
                <li key={service.to}>
                  <Link to={service.to} className="text-white/40 hover:text-white text-sm transition-colors duration-300 hover:pl-1">
                    {service.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-6">Contactos</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone size={16} className="text-[#C41E3A] mt-0.5 flex-shrink-0" />
                <a href={`tel:${companyInfo.phone}`} onClick={() => trackEvent('phone_click', { location: 'footer' })} className="lg:hidden text-white/60 text-sm hover:text-white transition-colors">
                  {companyInfo.phoneDisplay}
                </a>
                <span className="hidden lg:inline text-white/60 text-sm">
                  {companyInfo.phoneDisplay}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="text-[#C41E3A] mt-0.5 flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="text-white/60 text-sm hover:text-white transition-colors">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-[#C41E3A] mt-0.5 flex-shrink-0" />
                <a href="https://www.google.com/maps?q=39.01448,-8.97085" target="_blank" rel="noopener noreferrer" className="text-white/60 text-sm hover:text-white transition-colors">
                  {companyInfo.address}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={16} className="text-[#C41E3A] mt-0.5 flex-shrink-0" />
                <div className="text-white/60 text-sm">
                  <p>Seg-Sex: {companyInfo.workingHours.weekdays}</p>
                  <p>Sáb: {companyInfo.workingHours.saturday}</p>
                  <p>Dom: {companyInfo.workingHours.sunday}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr_1fr] gap-6">
            <div>
              <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-4">Informação Legal</h4>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                <div>
                  <dt className="text-white/30">Entidade</dt>
                  <dd className="text-white/60">{companyInfo.legalName}</dd>
                </div>
                <div>
                  <dt className="text-white/30">NIF/NIPC</dt>
                  <dd className="text-white/60">{companyInfo.nif}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-white/30">Sede / Estabelecimento</dt>
                  <dd className="text-white/60">{companyInfo.fullAddress}</dd>
                </div>
              </dl>
            </div>

            <div>
              <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-4">Consumidor</h4>
              <div className="space-y-3">
                <a
                  href={companyInfo.complaintsBookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/55 hover:text-white text-sm transition-colors"
                >
                  <FileText size={15} className="text-[#C41E3A]" />
                  Livro de Reclamações
                  <ExternalLink size={12} className="text-white/30" />
                </a>
                <a
                  href={companyInfo.adrUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/55 hover:text-white text-sm transition-colors"
                >
                  <Scale size={15} className="text-[#C41E3A]" />
                  {companyInfo.adrName}
                  <ExternalLink size={12} className="text-white/30" />
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-4">Políticas</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                {legalLinks.map(link => (
                  <Link key={link.to} to={link.to} className="text-white/45 hover:text-white text-sm transition-colors">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/30 text-xs">
            © {new Date().getFullYear()} {companyInfo.legalName}. Todos os direitos reservados.
          </p>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-white/30">
            {legalLinks.slice(1, 4).map(link => (
              <Link key={link.to} to={link.to} className="hover:text-white/50 transition-colors">
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-6 text-center">
          <a
            href="https://flowautomate.pt"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/20 hover:text-white/40 text-[11px] tracking-wide transition-colors"
          >
            Desenvolvido por flowautomate.pt
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
